/* The accounts a user may enter, built from the membership list the identity provider keeps in the
 * browser. It deliberately avoids the provider's own types, which disagree with what its endpoints
 * actually return, and describes only the two fields that list reliably carries. */

export interface DirectMembership {
  tenantId: string
  name?: string | null
}

export interface AccountOption {
  tenantId: string
  name: string
}

const byName = (a: AccountOption, b: AccountOption): number =>
  a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })

/* One row per account the user belongs to, the current account first and the rest alphabetical. An
 * account the provider leaves unnamed falls back to its tenant id so no row renders blank.
 *
 * Membership is the whole list on purpose: the provider refuses a switch into an account the user
 * does not belong to, and granting them that membership adds the account here anyway. */
export const buildAccountList = (
  memberships?: DirectMembership[] | null,
  activeTenantId?: string | null
): AccountOption[] => {
  const names = new Map<string, string>()

  memberships?.forEach(({ tenantId, name }) => {
    if (!tenantId || names.get(tenantId)) return
    names.set(tenantId, name?.trim() || '')
  })

  const accounts = Array.from(names, ([tenantId, name]) => ({ tenantId, name: name || tenantId }))
  const current = accounts.filter((account) => account.tenantId === activeTenantId)
  const rest = accounts.filter((account) => account.tenantId !== activeTenantId).sort(byName)

  return [...current, ...rest]
}

/* The switcher exists only for people with somewhere to switch to. */
export const canSwitchAccounts = (accounts: AccountOption[]): boolean => accounts.length > 1
